/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { Provider } from "react-redux";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { store } from "./redux/store";
import { LenditApp } from "./LenditApp";

import "./styles/main-styles.css";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import { Amplify } from "aws-amplify";
import DynamicMetadata from "@components/DynamicMetadata";
import ScriptsComponent from "@components/ScriptsComponent";
import DynamicThemeProvider from "@components/DynamicThemeProvider";
import { getAmplifyConfigByPath, getConfigName } from "@helpers/funciones";
import * as Sentry from "@sentry/react";
import { THEMES } from "@theme/index";

const initializeSenty = () => {
  if (import.meta.env.VITE_ENV === "localhost") {
    return;
  }

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    tracePropagationTargets: ["localhost"],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.VITE_ENV,
  });
};

initializeSenty();

const environment = import.meta.env.VITE_ENV === "PROD" ? "prod" : "dev";
const configName = getConfigName(window.location.href);
const { CustomTheme, amplifyConfig } = THEMES[configName as keyof typeof THEMES];
const persistor = persistStore(store);

const loginType = getAmplifyConfigByPath(window.location.pathname);

const authDetails = amplifyConfig[environment][loginType];

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: String(authDetails.userPoolId),
      userPoolClientId: String(authDetails.userPoolClientId),
      loginWith: {
        email: true,
      },
      signUpVerificationMethod: "code",
      userAttributes: {
        email: {
          required: true,
        },
      },
    },
  },
});

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider store={store}>
        <DynamicMetadata />
        <ScriptsComponent />
        <PersistGate loading={<></>} persistor={persistor}>
          <DynamicThemeProvider>
            <BrowserRouter>
              <LenditApp configName={configName} />
            </BrowserRouter>
          </DynamicThemeProvider>
        </PersistGate>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>,
);
